<template>
    <dialog-box v-if="value" @close="$emit('input', false)" width="500px"
                title="Edit Unifonic WhatsApp credentials">
        <div>
            <p>Rotate the credentials and/or phone for <strong>{{ profile.handle }}</strong>.</p>

            <unifonic-credentials-form v-model="creds" @valid="v => valid = v"
                :existing-handle-ids="otherWhatsappHandleIds"
                :existing-names-and-handles="otherWhatsappNamesAndHandles" />

            <div v-if="error" class="row-fluid">
                <div class="span12">
                    <div class="alert">{{ error }}</div>
                </div>
            </div>
        </div>
        <template #buttons>
            <a class="btn cancel" @click="$emit('input', false)">Cancel</a>
            <a class="btn btn-primary" @click="ok" :class="{disabled: saving || !valid}">
                <span v-if="saving" class="spinner"/>
                <span v-else>Save</span>
            </a>
        </template>
    </dialog-box>
</template>

<script>
import DialogBox from "@/components/DialogBox";
import UnifonicCredentialsForm from "@/setup/profiles/add-profile/whatsapp/UnifonicCredentialsForm";
import { mapActions, mapState } from "vuex";
import VuexStore from "@/store/vuex/VuexStore";
import { profileTypes } from "@/setup/profiles/ProfileUtils";

const UNIFONIC_APP_ID = 87;

function unpackTokenSecret(packed) {
    if (!packed) return { apiSecret: "", webhookSecret: "" };
    const first = packed.indexOf("::");
    const next = first >= 0 ? packed.indexOf("::", first + 2) : -1;
    if (first < 0 || next >= 0) return { apiSecret: packed, webhookSecret: "" };
    return { apiSecret: packed.substring(0, first), webhookSecret: packed.substring(first + 2) };
}

export default {
    name: "EditUnifonicCredentialsDialog",
    store: VuexStore,
    components: { DialogBox, UnifonicCredentialsForm },

    props: {
        value: Boolean,
        profile: { type: Object, required: true }
    },

    data() {
        const { apiSecret, webhookSecret } = unpackTokenSecret(this.profile.tokenSecret);
        return {
            creds: {
                name: this.profile.name || "",
                phone: this.profile.handleId || "",
                applicationId: this.profile.token || "",
                publicId: this.profile.refreshToken || "",
                apiSecret,
                webhookSecret
            },
            valid: false,
            saving: false,
            error: null
        }
    },

    computed: {
        ...mapState('profiles', ['profiles']),

        otherWhatsappHandleIds() {
            return (this.profiles || [])
                .filter(p => p.type === profileTypes.whatsapp && p.id !== this.profile.id)
                .map(p => p.handleId);
        },

        otherWhatsappNamesAndHandles() {
            const out = [];
            (this.profiles || []).forEach(p => {
                if (p.type !== profileTypes.whatsapp) return;
                if (p.id === this.profile.id) return;
                if (p.name) out.push(p.name);
                if (p.handle && p.handle !== p.name) out.push(p.handle);
            });
            return out;
        }
    },

    methods: {
        ...mapActions('profiles', ['updateProfile', 'refreshProfiles']),

        async ok() {
            if (this.saving || !this.valid) return;
            try {
                this.saving = true;
                this.error = null;

                let payload = {
                    id: this.profile.id,
                    type: this.profile.type,
                    name: this.creds.name,
                    handle: this.creds.name,
                    handleId: this.creds.phone,
                    token: this.creds.applicationId,
                    tokenSecret: `${this.creds.apiSecret}::${this.creds.webhookSecret}`,
                    refreshToken: this.creds.publicId,
                    apiAppId: UNIFONIC_APP_ID
                };

                await this.updateProfile(payload);
                await this.refreshProfiles(true);
                this.$emit('input', false);
            } catch (e) {
                console.error("Error updating Unifonic credentials: ", e);
                this.error = "Failed to update credentials. Please try again or contact DataEQ support.";
            } finally {
                this.saving = false;
            }
        }
    }
}
</script>
